import { USER_API_BASE, SCHOOLS_API_BASE, PASSES_API_BASE } from './config';
import { getToken, clearToken } from './tokenStore';

// ---------------------------------------------------------------------------
// Status-code semantics (hallpass-backend, uniform across all three services)
// ---------------------------------------------------------------------------
// 400 = schema validation failure (may carry an additional {errors} field)
// 401 = session missing/invalid — caller must re-authenticate
// 403 = authenticated but not allowed (role/membership failure)
// 404 = a server-resolved resource does not exist
// 409 = resource is in the wrong state for the requested transition
// 422 = business-rule or body-reference validation failure
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  status: number;
  errors: unknown;
  /**
   * The parsed error body, verbatim (null when the response wasn't JSON).
   * `message`/`errors` above cover the common envelope; a few endpoints
   * carry extra structured detail a caller has to act on — e.g. the roster
   * PUT's 409 lists which students would be moved out of another teacher's
   * class — and that detail has nowhere else to survive the throw.
   */
  body: unknown;

  constructor(status: number, message: string, errors?: unknown, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
    this.body = body ?? null;
  }
}

// ---------------------------------------------------------------------------
// Error body shapes (hallpass-backend, uniform across all three services)
// ---------------------------------------------------------------------------
// user-api/schools-api/passes-api's own error middleware returns the app
// envelope: { message, errors? }.
// Everything under /api/auth/* is handled by better-auth instead, which
// returns its own shape: { code, message } — `code` is the stable
// machine-readable identifier; `message` is human-readable but not
// contractually guaranteed to be present, so it must not be assumed. Both
// shapes are parsed explicitly below rather than relying on the coincidence
// that they currently both happen to carry `.message`.
// ---------------------------------------------------------------------------
const AUTH_PATH_PREFIX = '/api/auth/';

// The two error-body shapes this backend can return, unified. `code` is
// better-auth's stable identifier (unused today); `errors` is the app
// envelope's optional per-field validation detail (arbitrary JSON, hence
// `unknown`). A body that isn't valid JSON is represented as `null`.
interface ErrorBody {
  message?: string;
  errors?: unknown;
  code?: string;
}

/**
 * Extracts { message, errors } from a parsed error response body, branching
 * explicitly on the two response shapes this backend can return (see above).
 *
 * @param path - the request path, used to identify better-auth responses
 *   (anything under /api/auth/)
 * @param json - the parsed error body, or null if the body wasn't valid JSON
 * @param statusText - fallback message when the body has none
 */
function parseErrorBody(
  path: string,
  json: ErrorBody | null,
  statusText: string
): { message: string; errors: unknown } {
  if (!json) {
    return { message: statusText, errors: undefined };
  }

  if (path.startsWith(AUTH_PATH_PREFIX)) {
    // better-auth shape: { code, message }. Fall back to statusText if a
    // future better-auth version ever drops `message` in favor of `code`
    // alone — this app has no use for `code` today, so it isn't surfaced.
    return { message: json.message || statusText, errors: undefined };
  }

  // App envelope shape: { message, errors? }.
  return { message: json.message || statusText, errors: json.errors };
}

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export function onUnauthorized(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

// Endpoints whose 401 responses must never trigger the global logout flow.
// change-password can return 401 for "wrong current password" (status TBD
// pending backend confirmation), which is not a session-expiry condition —
// forcing a logout there would silently break the change-password UI. The
// call site (AuthContext.js's updatePassword) is out of scope for this
// change, so this is enforced here instead of via the `suppress401Handler`
// per-request option below.
//
// Exported so callers that must independently reference this exact path
// (e.g. AuthContext.js's updatePassword) use the same constant instead of
// duplicating the literal.
export const CHANGE_PASSWORD_PATH = '/api/auth/change-password';

const ALWAYS_SUPPRESS_401_PATHS = new Set<string>([CHANGE_PASSWORD_PATH]);

export interface ApiFetchOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  suppress401Handler?: boolean;
}

export async function apiFetch<T = unknown>(
  base: string,
  path: string,
  { method = 'GET', body, headers = {}, suppress401Handler = false }: ApiFetchOptions = {}
): Promise<T> {
  const finalHeaders: Record<string, string> = { ...headers };
  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  const token = getToken();
  if (token) {
    finalHeaders['Authorization'] = 'Bearer ' + token;
  }

  const res = await fetch(base + path, {
    method,
    credentials: 'include',
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let json: ErrorBody | null = null;
    try {
      json = await res.json();
    } catch {
      // Non-JSON error body — fall back to statusText below.
    }

    const suppress401 =
      suppress401Handler || ALWAYS_SUPPRESS_401_PATHS.has(path);

    if (res.status === 401 && !suppress401) {
      clearToken();
      if (unauthorizedHandler) {
        unauthorizedHandler();
      }
    }

    const { message, errors } = parseErrorBody(path, json, res.statusText);
    throw new ApiError(res.status, message, errors, json);
  }

  if (res.status === 204) {
    // No content: callers (e.g. DELETE) rely on receiving null here. The
    // generic caller declares the expected shape; a 204 body is empty.
    return null as T;
  }

  try {
    return (await res.json()) as T;
  } catch {
    // 2xx with a non-JSON (or empty) body. Mirror the guarded error path
    // above rather than letting res.json()'s raw SyntaxError escape: callers
    // branch on `error.status`, and a bare SyntaxError carries none. Fall
    // back to statusText for the message, exactly as parseErrorBody does.
    throw new ApiError(res.status, res.statusText, undefined);
  }
}

export function userApi<T = unknown>(path: string, opts?: ApiFetchOptions): Promise<T> {
  return apiFetch<T>(USER_API_BASE, path, opts);
}

export function schoolsApi<T = unknown>(path: string, opts?: ApiFetchOptions): Promise<T> {
  return apiFetch<T>(SCHOOLS_API_BASE, path, opts);
}

export function passesApi<T = unknown>(path: string, opts?: ApiFetchOptions): Promise<T> {
  return apiFetch<T>(PASSES_API_BASE, path, opts);
}

/**
 * Serializes a params object into a query string. Array values are joined
 * with commas (the convention every hallpass-backend list endpoint uses for
 * multi-value filters like status/ids/role); undefined, null, and empty-string
 * values are omitted entirely.
 *
 * @returns '' or '?key=value&...'
 */
export function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return;
    }
    usp.set(key, Array.isArray(value) ? value.join(',') : String(value));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}
